/**
 * Record kinds. Content kinds are owned by Fruuit (shared with subscribers when
 * `client_org_id` is NULL, D-03). Private kinds belong to one investor org.
 * Internal kinds never reach investors (AUTHZ-007).
 */
export const CONTENT_KINDS = [
  'issuers',
  'securities',
  'sources',
  'incidents',
  'assessments',
  'engagements',
  'objectives',
  'milestones',
  'dialogues',
  'meetings',
  'minutes',
] as const
export type ContentKind = (typeof CONTENT_KINDS)[number]

export const INTERNAL_KINDS = ['search_runs', 'feed_signals'] as const
export type InternalKind = (typeof INTERNAL_KINDS)[number]

export const PRIVATE_KINDS = [
  'portfolios',
  'holdings',
  'questions',
  'participation_requests',
  'client_actions',
  'saved_views',
  'preferences',
] as const
export type PrivateKind = (typeof PRIVATE_KINDS)[number]

/** Kinds with the draft -> in_review -> approved -> published workflow. */
export const REVIEWED_KINDS = ['assessments', 'milestones', 'dialogues', 'minutes'] as const
export type ReviewedKind = (typeof REVIEWED_KINDS)[number]

/** Kinds whose investor-visible fields pass a release gate (D-06). */
export const RELEASE_GATED_KINDS = ['engagements', 'objectives'] as const
export type ReleaseGatedKind = (typeof RELEASE_GATED_KINDS)[number]

/** Content released per engagement subscription; the rest is per authorized company. */
export const ENGAGEMENT_SCOPED_KINDS = [
  'engagements',
  'objectives',
  'milestones',
  'dialogues',
  'meetings',
  'minutes',
] as const satisfies readonly ContentKind[]

/** Kinds released by the assigned case reviewer only (WF-007, CAL-047). */
export const CASE_REVIEWED_KINDS = ['milestones', 'dialogues', 'minutes'] as const

export type RecordKind = ContentKind | InternalKind | PrivateKind

export function isContentKind(kind: string): kind is ContentKind {
  return (CONTENT_KINDS as readonly string[]).includes(kind)
}

export function isInternalKind(kind: string): kind is InternalKind {
  return (INTERNAL_KINDS as readonly string[]).includes(kind)
}

export function isEngagementScopedKind(kind: string): boolean {
  return (ENGAGEMENT_SCOPED_KINDS as readonly string[]).includes(kind)
}
